<template>
    <FetchSavedTemplatesByPage
        v-slot="{
            results: templates,
            isLoading,
            isFullyLoaded,
            fetchPage,
            reload,
            error,
        }"
        :query="query"
        :page-size="25"
        :variables="variables"
        @error="handleLoadError"
        @templates-fetched.once="onTemplatesFetch"
    >
        <div>
            <TemplateSelectionPanelActions
                v-slot="{
                    handleFromScratchTemplateSelected,
                    handleFromSavedTemplateSelected,
                    creatingTemplateId,
                    deleteSavedTemplate,
                    deletingTemplateId,
                    handleTemplatePreview,
                    closeTemplatePreview,
                    previewTemplate,
                    previewTemplateHtml,
                    isLoadingPreviewSelection,
                    isCreating,
                    isDeleting,
                    allowSelection
                }"
                :marketing-email-id="marketingEmailId"
                :source="source"
                :template-id="templateId"
                :unlayer-email-logo-tool-enabled="unlayerEmailLogoToolEnabled"
                @item-deleted="handleItemDeleted(reload)"
                @item-delete-error="handleItemDeleteError(reload)"
                @template-selected="$emit('template-selected', $event)"
                @template-created="$emit('template-created', $event)"
            >
                <div class="cards-layout-container">
                    <TemplateGalleryCardsLayout
                        :error="error"
                        :is-fully-loaded="isFullyLoaded"
                        :is-loading="isLoading"
                        :items="templates"
                        :page-size="25"
                        @fetch="fetchPage"
                    >
                        <template #filters>
                            <MyTemplatesFilters
                                v-if="showTemplateFilters"
                                :search-term.sync="templateSearchTerm"
                                :filter-email-type.sync="filterEmailType"
                                :show-email-type-dropdown="showEmailTypeDropdown"
                                :show-search-bar="!isMaxClassic"
                                data-qa="my-templates-filters"
                            />
                        </template>

                        <template #empty>
                            <EmptyState
                                v-if="!templateSearchTerm"
                                class="saved-panel-empty-state"
                                title="You haven't created a template yet"
                                description="Select a template from the template gallery or create your own from there"
                                image="email-broadcasts"
                            >
                                <DsFilledButton
                                    :loading="isCreating"
                                    @click="$emit('attempt-to-select-template', { callback: handleFromScratchTemplateSelected, type: 'scratch', template: {} })"
                                >
                                    {{ $t('email.broadcast.unlayer.template.selection.common.startFromScratch') }}
                                </DsFilledButton>

                                <DsTextButton
                                    class="empty-state-back-button"
                                    @click="goToGallery"
                                >
                                    {{ $t('email.broadcast.unlayer.template.selection.common.goBackToGallery') }}
                                </DsTextButton>
                            </EmptyState>

                            <template v-else>
                                <div class="no-search-results-container">
                                    <p> {{ $t('unlayerBuilder.templateSelection.savedTemplates.noSearchResults') }} </p>

                                    <DsButton
                                        class="start-from-scratch-btn"
                                        :loading="isCreating"
                                        @click="$emit('attempt-to-select-template', { callback: handleFromScratchTemplateSelected, type: 'scratch', template: {} })"
                                    >
                                        {{ $t('email.broadcast.unlayer.template.selection.common.startFromScratch') }}
                                    </DsButton>
                                </div>
                            </template>
                        </template>

                        <template #static>
                            <StartFromScratchCard
                                :loading="creatingTemplateId === 'scratch'"
                                :disabled="isCreating"
                                :emails-thryv-theme-enabled="emailsThryvThemeEnabled"
                                @select="$emit('attempt-to-select-template', { callback: handleFromScratchTemplateSelected, type: 'scratch', template: {} })"
                            />
                        </template>

                        <template #card="{ item: template }">
                            <TemplateGalleryBardCard
                                v-if="isBardTemplate(template)"
                                :key="`bard-${template.id}`"
                                :selected="template.id === creatingTemplateId || template.id === deletingTemplateId"
                                :disabled="isCreating || isDeleting"
                                :template="template"
                                :thryv-interim-unified-theme-enabled="thryvInterimUnifiedThemeEnabled"
                                @preview="handleTemplatePreview(template)"
                                @delete-template="deleteBardTemplate(template, reload)"
                                @migrate-to-unlayer="openConvertEmailDialog(template)"
                            />

                            <TemplateGalleryUnlayerCard
                                v-else
                                :key="`unlayer-${template.id}`"
                                :template="template"
                                :selected="template.id === creatingTemplateId || template.id === deletingTemplateId"
                                :disabled="isCreating || isDeleting"
                                :preview-enabled="previewEnabled"
                                @click="$emit('attempt-to-select-template', { callback: handleFromSavedTemplateSelected, type: 'saved', template })"
                                @preview="handleTemplatePreview(template)"
                            >
                                <template #menu>
                                    <GalleryCardMenu
                                        show-rename
                                        @delete="confirmDelete(template, deleteSavedTemplate)"
                                        @rename="handleRenameTemplateModalOpen(template)"
                                    />
                                </template>
                            </TemplateGalleryUnlayerCard>
                        </template>
                    </TemplateGalleryCardsLayout>

                    <ContentPreviewModal
                        v-if="previewEnabled"
                        data-qa="campaign-email-template-preview-modal"
                        :preview-html="previewTemplateHtml"
                        :preview-image-url="getPreviewImageUrl(previewTemplate, templates)"
                        @close="closeTemplatePreview"
                    >
                        <DsFilledButton
                            v-if="allowSelection"
                            data-qa="use-this-template"
                            :loading="isLoadingPreviewSelection"
                            @click="$emit('attempt-to-select-template', { callback: handleFromSavedTemplateSelected, type: 'saved', template: previewTemplate })"
                        >
                            {{ $t('email.broadcast.unlayer.template.selection.common.useThisTemplate') }}
                        </DsFilledButton>
                    </ContentPreviewModal>
                </div>
            </TemplateSelectionPanelActions>

            <template v-if="isRenameTemplateModalOpen">
                <portal v-if="isCampaign" to="unlayer-email-editor-root">
                    <RenameTemplateModal
                        :template="templateToRename"
                        @close="handleRenameTemplateModalClose"
                        @saved="handleRenameTemplateModalSaved(templates, $event)"
                    />
                </portal>

                <RenameTemplateModal
                    v-else
                    :template="templateToRename"
                    @close="handleRenameTemplateModalClose"
                    @saved="handleRenameTemplateModalSaved(templates, $event)"
                />
            </template>

            <ConvertEmailDialog
                v-if="showEmailConvertModal"
                :design="bardTemplateToConvert.design"
                :html="bardTemplateToConvert.html"
                @close="showEmailConvertModal = false"
                @converted="migrateToUnlayer"
            />
        </div>
    </FetchSavedTemplatesByPage>
</template>

<script>
import FetchSavedTemplatesByPage from './FetchSavedTemplatesByPage';
import ConvertEmailDialog from './ConvertEmailDialog';
import TemplateSelectionPanelActions from './TemplateSelectionPanelActions';
import TemplateGalleryCardsLayout from './TemplateGalleryCardsLayout';
import TemplateGalleryUnlayerCard from './TemplateGalleryUnlayerCard';
import TemplateGalleryBardCard from './TemplateGalleryBardCard';
import ContentPreviewModal from './ContentPreviewModal';
import StartFromScratchCard from './StartFromScratchCard';
import EmptyState from '@/components/shared/EmptyState';
import GalleryCardMenu from './GalleryCardMenu';
import RenameTemplateModal from './RenameTemplateModal';
import MyTemplatesFilters from './MyTemplatesFilters';
import { SOURCES } from '@/components/unlayer/constants/unlayer.constants';
import { UNLAYER_EMAIL_FUNNEL_BASE_URL, updateBardTemplateMigrationStatus, updateBardPrivateTemplateMigrationStatus } from '@/api/campaigns/campaignUnlayerEmail';
import { UNLAYER_BROADCAST_BASE_URL } from '@/api/broadcasts/emailBroadcast';
import {
    AGGREGATED_EMAIL_TEMPLATES_FILTER_OPTIONS as FILTER_OPTIONS,
    TEMPLATE_TYPE,
    DEFAULT_TEMPLATE_HTML,
    BARD_TEMPLATE_MIGRATION_STATUS as MIGRATIION_STATUS,
} from '@/components/broadcast/constants/emailBroadcast.constants';

const ERROR_MESSAGE = 'For some reason your templates didn’t load. Try refreshing the page.';
const CLOSE_LABEL = 'Ok, got it';

export default {
    name: 'TemplateSelectionSavedPanel',

    components: {
        TemplateSelectionPanelActions,
        TemplateGalleryCardsLayout,
        TemplateGalleryUnlayerCard,
        TemplateGalleryBardCard,
        ContentPreviewModal,
        ConvertEmailDialog,
        StartFromScratchCard,
        EmptyState,
        FetchSavedTemplatesByPage,
        GalleryCardMenu,
        RenameTemplateModal,
        MyTemplatesFilters,
    },

    props: {
        marketingEmailId: String,
        previewEnabled: Boolean,
        source: {
            type: String,
            default: SOURCES.CAMPAIGN,
        },
        templateId: String,
        isMaxClassic: Boolean,
        unlayerEmailLogoToolEnabled: Boolean,
        emailsThryvThemeEnabled: Boolean,
        thryvInterimUnifiedThemeEnabled: Boolean,
    },

    data() {
        return {
            filterEmailType: FILTER_OPTIONS.ALL,
            isRenameTemplateModalOpen: false,
            templateToRename: null,
            showEmailConvertModal: false,
            showTemplateFilters: false,
            bardTemplateToConvert: null,
            templateSearchTerm: null,
        };
    },

    computed: {
        isCampaign() {
            return this.source === SOURCES.CAMPAIGN;
        },

        isBroadcast() {
            return this.source === SOURCES.BROADCAST;
        },


        query() {
            let url = '';

            if (this.isBroadcast) {
                url = `${UNLAYER_BROADCAST_BASE_URL}/savedTemplates`;
            } else if (this.isCampaign) {
                url = `${UNLAYER_EMAIL_FUNNEL_BASE_URL}/savedTemplates`;
            }

            return {
                url,
                method: 'get',
            };
        },

        variables() {
            return {
                filter: this.filterEmailType,
                search: this.templateSearchTerm,
            };
        },
    },

    methods: {
        goToGallery() {
            this.$emit('goToGallery');
        },

        onTemplatesFetch(templates) {
            this.showEmailTypeDropdown = templates?.some(this.isBardTemplate);
            this.showTemplateFilters = true;
        },

        async confirmDelete(template, deleteSavedTemplate) {
            try {
                await this.$confirm({
                    destructive: true,
                    title: this.$t('global.reallyDelete'),
                    message: this.$t('email.broadcast.unlayer.template.questions.reallyDeleteTemplate'),
                    confirmLabel: this.$t('global.ok'),
                    cancelLabel: this.$t('global.cancel'),
                });

                deleteSavedTemplate(template);
            } catch (error) {
                // do nothing
            }
        },

        handleItemDeleted(reload) {
            this.$toast({ message: 'Your template has been deleted' });
            reload();
        },

        handleItemDeleteError(reload) {
            this.$error({
                bottom: true,
                message: this.$t('email.broadcast.unlayer.template.error.deleteTemplateFailed'),
                showIcon: true,
            });
            reload();
        },

        handleRenameTemplateModalOpen(template) {
            this.templateToRename = template;
            this.isRenameTemplateModalOpen = true;
        },

        handleRenameTemplateModalSaved(templates, $event) {
            const template = templates.find(({ id }) => id === this.templateToRename.id);

            template.name = $event.name;

            this.handleRenameTemplateModalClose();
        },

        handleRenameTemplateModalClose() {
            this.templateToRename = null;
            this.isRenameTemplateModalOpen = false;
        },

        handleLoadError() {
            this.$error({
                bottom: true,
                closeLabel: CLOSE_LABEL,
                message: ERROR_MESSAGE,
                showIcon: true,
            });
        },

        isBardTemplate(template) {
            const { templateType } = template;

            return (templateType === TEMPLATE_TYPE.BARD_SENT || templateType === TEMPLATE_TYPE.BARD_PRIVATE);
        },

        async deleteBardTemplate({ id, templateType }, reload) {
            try {
                await this.$confirm({
                    destructive: true,
                    title: this.$t('global.reallyDelete'),
                    message: this.$t('email.broadcast.unlayer.template.questions.reallyDeleteTemplate'),
                    confirmLabel: this.$t('global.ok'),
                    cancelLabel: this.$t('global.cancel'),
                });
            } catch (e) {
                return;
            }

            try {
                await this.updateBardTemplateMigrationStatusByTemplateType({ id, templateType, migrationStatus: MIGRATIION_STATUS.MANUALLY_ARCHIVED });
            } catch (e) {
                this.$error({
                    message: this.$t('email.broadcast.unlayer.template.error.deleteTemplateFailed'),
                    showIcon: true,
                    bottom: true,
                });

                return;
            }

            reload();
        },

        openConvertEmailDialog(template) {
            this.bardTemplateToConvert = template;
            this.showEmailConvertModal = true;
        },

        migrateToUnlayer({ design }) {
            const parsedDesign = JSON.parse(design);

            const { id, templateType } = this.bardTemplateToConvert;

            this.$emit('template-selected', {
                design: parsedDesign,
                html: DEFAULT_TEMPLATE_HTML,
                name: this.bardTemplateToConvert.name,
            });

            this.$emit('create-saved-template', {
                design: parsedDesign,
                name: this.bardTemplateToConvert.name,
            });

            this.showEmailConvertModal = false;

            this.updateBardTemplateMigrationStatusByTemplateType({ id, templateType, migrationStatus: MIGRATIION_STATUS.MIGRATED_TO_UNLAYER });
        },

        async updateBardTemplateMigrationStatusByTemplateType({ id, templateType, migrationStatus }) {
            if (templateType === TEMPLATE_TYPE.BARD_PRIVATE) {
                await updateBardPrivateTemplateMigrationStatus({
                    templateId: id,
                    migrationStatus,
                });
            } else {
                await updateBardTemplateMigrationStatus({
                    templateId: id,
                    migrationStatus,
                });
            }
        },

        getPreviewImageUrl(previewTemplate, templates) {
            return previewTemplate?.id
                ? templates.find((t) => t.id === previewTemplate.id)?.htmlPreviewUrl || null
                : null;
        },
    },
};
</script>

<style lang="scss" scoped>
.cards-layout-container {
    width: 100%;
}

.saved-panel-empty-state {
    padding-top: 6rem;
}

.empty-state-back-button {
    @include margin-start($spacing-100);
}

.no-search-results-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $spacing-400;
    margin-top: $spacing-400;

    p {
        color: $color-text-subtle;
        font-size: $font-size-md;
        margin: 0;
    }

    .start-from-scratch-btn {
        width: fit-content;
    }
}

</style>
